import { create } from "zustand";

export type ObjectDrawerType =
  | "accounts"
  | "users"
  | "email"
  | "meeting"
  | "doc"
  | "concept"
  | "task"
  | "note"
  | "custom-object";

export type DrawerOpenPayload = {
  objectType: ObjectDrawerType;
  objectId: string;
  objectSlug?: string;
  preloadedData?: Record<string, any> | null;
};

type ObjectDrawerState = {
  isOpen: boolean;
  objectType: ObjectDrawerType | null;
  objectId: string | null;
  objectSlug: string | null;
  preloadedData: Record<string, any> | null;
  history: DrawerOpenPayload[];
  openDrawer: (payload: DrawerOpenPayload) => void;
  goBack: () => void;
  closeDrawer: () => void;
};

const useObjectDrawerStore = create<ObjectDrawerState>((set, get) => ({
  isOpen: false,
  objectType: null,
  objectId: null,
  objectSlug: null,
  preloadedData: null,
  history: [],

  openDrawer: (payload) => {
    const state = get();
    // If drawer is already open, push current state to history
    if (state.isOpen && state.objectType && state.objectId) {
      const currentEntry: DrawerOpenPayload = {
        objectType: state.objectType,
        objectId: state.objectId,
        objectSlug: state.objectSlug ?? undefined,
        preloadedData: state.preloadedData,
      };
      set({
        isOpen: true,
        objectType: payload.objectType,
        objectId: payload.objectId,
        objectSlug: payload.objectSlug ?? null,
        preloadedData: payload.preloadedData ?? null,
        history: [...state.history, currentEntry],
      });
    } else {
      set({
        isOpen: true,
        objectType: payload.objectType,
        objectId: payload.objectId,
        objectSlug: payload.objectSlug ?? null,
        preloadedData: payload.preloadedData ?? null,
        history: [],
      });
    }
  },

  goBack: () => {
    const state = get();
    if (state.history.length === 0) {
      // No history, close drawer
      set({
        isOpen: false,
        objectType: null,
        objectId: null,
        objectSlug: null,
        preloadedData: null,
        history: [],
      });
      return;
    }
    const prev = state.history[state.history.length - 1];
    set({
      isOpen: true,
      objectType: prev.objectType,
      objectId: prev.objectId,
      objectSlug: prev.objectSlug ?? null,
      preloadedData: prev.preloadedData ?? null,
      history: state.history.slice(0, -1),
    });
  },

  closeDrawer: () =>
    set({
      isOpen: false,
      objectType: null,
      objectId: null,
      objectSlug: null,
      preloadedData: null,
      history: [],
    }),
}));

export default useObjectDrawerStore;
